<template>
  <section
    class="relative bg-brand-dark min-h-screen flex flex-col items-center justify-center px-6 md:px-12 text-brand-cream paper-texture overflow-hidden pt-20 pb-16"
  >
    <!-- Ambient doodles -->
    <SketchDoodle type="star" :size="18" top="12%" left="8%" motion="float" :opacity="0.55" hide-on-mobile />
    <SketchDoodle type="spark" :size="22" top="18%" right="12%" motion="drift" :opacity="0.45" hide-on-mobile />
    <SketchDoodle type="dots" :size="36" top="28%" left="4%" :opacity="0.4" hide-on-mobile />
    <SketchDoodle type="flower" :size="26" bottom="18%" left="10%" motion="wiggle" :opacity="0.4" hide-on-mobile />
    <SketchDoodle type="cloud" :size="28" top="14%" left="42%" motion="drift" :opacity="0.35" hide-on-mobile />
    <SketchDoodle type="computer" :size="32" bottom="14%" right="8%" motion="float" :opacity="0.4" hide-on-mobile />
    <SketchDoodle type="code" :size="28" top="62%" right="4%" :rotate="-8" :opacity="0.35" hide-on-mobile />

    <div class="snap-start min-h-[calc(100vh-5rem)] flex flex-col items-start justify-center w-full max-w-6xl mx-auto relative z-10">
      <!-- Tiny status labels -->
      <div class="flex flex-wrap gap-3 mb-6 animate-enter">
        <span class="sketch-label sticker border border-brand-muted/40 px-2.5 py-0.5 rounded-sm bg-brand-surface/40">
          available for work
        </span>
        <span class="sketch-label sticker border border-brand-muted/30 px-2.5 py-0.5 rounded-sm">
          backend enthusiast
        </span>
        <span class="sketch-dev text-sm hidden sm:inline">// currently building</span>
      </div>

      <div class="relative mb-6 md:mb-10 animate-enter animate-enter-delay-1">
        <h1 class="text-3xl md:text-4xl font-bold tracking-tight">
          About <span class="sketch-underline">Me</span>
        </h1>
        <span class="sketch-note absolute -top-5 right-0 md:-right-16 md:top-0 rotate-6 text-brand-muted/80 hidden sm:block">
          hi there ✦
        </span>
      </div>

      <SketchDivider />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start w-full animate-enter animate-enter-delay-2">
        <!-- Paragraph -->
        <div class="order-2 md:order-none relative">
          <p class="sketch-dev text-sm mb-3" aria-hidden="true">// software engineer · backend · AI</p>
          <p class="text-base md:text-lg mb-4 text-brand-muted leading-relaxed">
            Hey, I'm Bisrat, a software engineer based in Ethiopia. I mostly live in the world of
            <span class="sketch-circle text-brand-cream font-medium">APIs</span>,
            databases, and making sure stuff doesn't break at 2am. Lately I've been deep into
            <span class="text-brand-cream font-medium sketch-underline">AI/LLM</span>
            stuff too.
          </p>
          <p class="text-base md:text-lg mb-4 text-brand-muted leading-relaxed">
            When I'm not staring at code I'm sketching, taking photos, or reading. Anything that keeps me curious honestly.
          </p>

          <!-- Margin-style personal note -->
          <p class="sketch-note mt-2 mb-6 pl-3 border-l border-brand-muted/40" aria-hidden="true">
            also: guitar · photography · sketching ↗
          </p>

          <div class="relative inline-flex flex-wrap space-x-4 mt-2 items-center">
            <a href="https://github.com/b-isry" target="_blank" rel="noopener" class="text-brand-cream hover:text-brand-muted transition" aria-label="GitHub">
              <i class="fab fa-github text-2xl"></i>
            </a>
            <a href="https://www.linkedin.com/in/bisrat-tesh/" target="_blank" rel="noopener" class="text-brand-cream hover:text-brand-muted transition" aria-label="LinkedIn">
              <i class="fab fa-linkedin text-2xl"></i>
            </a>
            <a href="https://instagram.com/b_isry" target="_blank" rel="noopener" class="text-brand-cream hover:text-brand-muted transition" aria-label="Instagram">
              <i class="fab fa-instagram text-2xl"></i>
            </a>
            <a href="https://t.me/b_isry" target="_blank" rel="noopener" class="text-brand-cream hover:text-brand-muted transition" aria-label="Telegram">
              <i class="fab fa-telegram text-2xl"></i>
            </a>
            <a href="https://leetcode.com/u/BisratT/" target="_blank" rel="noopener" class="text-brand-cream hover:text-brand-muted transition" aria-label="LeetCode">
              <i class="fa fa-code text-2xl"></i>
            </a>
            <a
              href="https://drive.google.com/file/d/1hIPHrMenhZXGXpULnkbpMJwMwUIPOPsy/view?usp=sharing"
              target="_blank"
              rel="noopener"
              class="relative border border-brand-cream text-xs px-2 py-1 rounded hover:bg-brand-cream hover:text-brand-dark transition duration-300"
              style="font-weight: 500;"
            >
              CV
            </a>
            <!-- Hand-drawn arrow pointing at CV CTA -->
            <SketchDoodle
              type="arrow"
              :size="28"
              inline
              motion="arrow"
              :opacity="0.65"
              class="ml-1 hidden sm:inline-flex"
            />
            <span class="sketch-label hidden sm:inline -ml-1 translate-y-1" aria-hidden="true">grab this</span>
          </div>
        </div>

        <!-- Polaroid picture -->
        <div class="relative flex items-center justify-center md:order-none order-1 animate-enter animate-enter-delay-3">
          <SketchDoodle type="star" :size="16" top="-8%" right="18%" motion="float" :opacity="0.6" />
          <SketchDoodle type="heart" :size="18" bottom="6%" left="8%" motion="wiggle" :opacity="0.45" hide-on-mobile />
          <div class="polaroid w-40 md:w-52 !pb-2.5">
            <div class="aspect-[3/4] overflow-hidden bg-brand-surface">
              <img src="../assets/images/me.jpg" alt="Bisrat Teshome" />
            </div>
          </div>
          <span
            class="sketch-note absolute -bottom-2 right-4 md:right-10 rotate-[-8deg] text-brand-muted/80"
            aria-hidden="true"
          >
            ★ that's me
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import SketchDoodle from "./SketchDoodle.vue";
import SketchDivider from "./SketchDivider.vue";

export default {
  name: "AboutMe",
  components: { SketchDoodle, SketchDivider },
};
</script>
